import {
  CommandBar,
  Label,
  Link,
  MessageBar,
  MessageBarType,
  Stack,
} from "@fluentui/react";
import { ExperimentFormContext } from "../../../Hooks/UseExperimentForm";
import VariantDetailsFormTable from "./VariantDetailsFormTable/VariantDetailsFormTable";
import VariantDetailPanel from "./VariantDetailPanel/VariantDetailPanel";
import { styles } from "./variantDetailsFormStyles";
import { styles as sharedFormStyles } from "../ExperimentDetailsForm/experimentDetailsForm.styles";
import { useState } from "react";
import {
  REPO_URL,
  VARIANT_DETAIL_CONTROL_KEY,
  VARIANT_DETAIL_TREATMENT_KEY,
} from "../../../Shared/shared.consts";
import { useBoolean } from "@fluentui/react-hooks";
import { ExperimentType, VariantDetail } from "../../../Types";
import { FormSectionError } from "../FormComponents";
import { VARIANT_FIELDS } from "../ExperimentDetailsForm/ExperimentDetailsValidation/experimentDetailsFormValidation";
import useAppInsights from "../../../Hooks/UseAppInsights";
import { NavType } from "../../../Shared/AppInsights";

interface VariantDetailsFormProps {
  /**
   * Form context containing form state, handlers, and validation logic
   */
  experimentFormContext: ExperimentFormContext;
  /**
   * List of all experiments for validation and context
   */
  experiments: ExperimentType[];
  /**
   * Validation errors keyed by field name. Used to show error indicator/counts on tabs.
   */
  errors?: Record<string, string[]>;
  /**
   * When true, forms are editable. When false, forms are read-only.
   */
  isEditMode?: boolean;
  /**
   * When true, form will have empty fields for creating new variant details
   */
  isCreate?: boolean;
  /**
   * When true, form will have pre-filled fields for cloning existing variant details
   */
  isClone?: boolean;
}

/**
 * Form component for managing experiment variants (control and treatment groups).
 * Allows adding, editing, and deleting variant configurations with feature flags.
 */
const VariantDetailsForm: React.FC<VariantDetailsFormProps> = ({
  experimentFormContext,
  experiments,
  errors = {},
  isEditMode = false,
  isCreate = false,
  isClone = false,
}) => {
  const { trackNavigationEvent, trackOperationEvent } =
    useAppInsights("VariantDetailsForm");
  const [selectedVariantType, setSelectedVariantType] = useState<string>("");
  const [selectedVariant, setSelectedVariant] = useState<
    VariantDetail | undefined
  >(undefined);
  const [
    variantDetailPanelIsOpen,
    { setTrue: openVariantDetailPanel, setFalse: dismissPanel },
  ] = useBoolean(false);
  const {
    formState: { variantDetails = [] },
  } = experimentFormContext;
  const controlVariant = variantDetails.filter((variant) => variant.isControl);
  const hasControl = controlVariant.length > 0;
  const treatmentVariants = variantDetails.filter(
    (variant) => !variant.isControl,
  );
  const disabled = !isEditMode && !isCreate;

  const onAddVariant = (type: string) => {
    openVariantDetailPanel();
    setSelectedVariant(undefined);
    setSelectedVariantType(type);
    trackOperationEvent({
      operation: "Add",
      function: "onAddVariant",
      properties: { variantType: type },
    });
  };

  const onEditVariant = (variantDetail: VariantDetail) => {
    openVariantDetailPanel();
    setSelectedVariant(variantDetail);
    setSelectedVariantType(
      variantDetail.isControl
        ? VARIANT_DETAIL_CONTROL_KEY
        : VARIANT_DETAIL_TREATMENT_KEY,
    );
    trackOperationEvent({
      operation: "Edit",
      function: "onEditVariant",
      properties: { variantDetail },
    });
  };

  const onSaveVariant = (variantDetail: VariantDetail) => {
    let updatedVariants = variantDetails;
    if (selectedVariant) {
      updatedVariants = updatedVariants.map((variant) =>
        variant.name === selectedVariant.name ? variantDetail : variant,
      );
    } else {
      updatedVariants = [...updatedVariants, variantDetail];
    }
    trackOperationEvent({
      operation: "Save",
      function: "onSaveVariant",
      properties: { updatedVariants },
    });
    experimentFormContext.handleFieldChange("variantDetails", updatedVariants);
    dismissPanel();
  };

  const onDeleteVariant = (variantDetail: VariantDetail) => {
    const updatedVariants = variantDetails.filter(
      (variant) => variant.name !== variantDetail.name,
    );
    experimentFormContext.handleFieldChange("variantDetails", updatedVariants);
    trackOperationEvent({
      operation: "Delete",
      function: "onDeleteVariant",
      properties: { variantDetail, updatedVariants },
    });
  };

  const onRemoveFlag = (variantDetail: VariantDetail, flag: string) => {
    const { featureFlags } = variantDetail;
    const featureFlagCopy = { ...featureFlags };
    delete featureFlagCopy[flag];
    const updatedVariant = {
      ...variantDetail,
      featureFlags: { ...featureFlagCopy },
    };
    const updatedVariants = variantDetails.map((variant) =>
      variant.name === updatedVariant.name ? updatedVariant : variant,
    );
    trackOperationEvent({
      operation: "FormChange",
      function: "onRemoveFlag",
      properties: {
        field: "variantDetails.featureFlags",
        value: variantDetail,
        flag,
        updatedVariant,
        updatedVariants,
      },
    });
    experimentFormContext.handleFieldChange("variantDetails", updatedVariants);
  };

  return (
    <Stack
      className={sharedFormStyles.formContainer}
      tokens={{ childrenGap: 20 }}
    >
      <MessageBar messageBarType={MessageBarType.info}>
        Variants are optional and only required if you need to use multiple
        feature names. Read more about these configurations
        <Link
          href={`${REPO_URL}?path=/CONFIG_SPEC.md&_a=preview&anchor=advanced-experiment-configuration`}
          target="_blank"
          onClick={() => {
            trackNavigationEvent({
              destination: `${REPO_URL}?path=/CONFIG_SPEC.md&_a=preview&anchor=advanced-experiment-configuration`,
              navType: NavType.External,
            });
          }}
        >
          here
        </Link>
        .
      </MessageBar>
      <FormSectionError errors={errors} extensionFieldErrors={VARIANT_FIELDS} />
      <Stack
        className={styles.variantDetailsFormContainer}
        tokens={{ childrenGap: 20 }}
      >
        <Stack className={styles.variantDetailContainer}>
          <Stack verticalAlign="center" horizontal style={{ padding: 0 }}>
            <Label className={styles.variantDetailsLabel}>
              Control Variant
            </Label>
            <CommandBar
              items={[
                {
                  key: "add",
                  text: "Control",
                  iconProps: { iconName: "Add", style: { fontSize: 12 } },
                  onClick: () => onAddVariant(VARIANT_DETAIL_CONTROL_KEY),
                  disabled: hasControl || disabled,
                },
              ]}
            />
          </Stack>
          <VariantDetailsFormTable
            isControl={true}
            variants={controlVariant}
            onDelete={onDeleteVariant}
            onEdit={onEditVariant}
            onRemoveFlag={onRemoveFlag}
            disabled={disabled}
          />
        </Stack>
        <Stack className={styles.variantDetailContainer}>
          <Stack verticalAlign="center" horizontal>
            <Label className={styles.variantDetailsLabel}>
              Treatment Variants
            </Label>
            <CommandBar
              items={[
                {
                  key: "add",
                  text: "Treatment",
                  iconProps: { iconName: "Add", style: { fontSize: 12 } },
                  onClick: () => onAddVariant(VARIANT_DETAIL_TREATMENT_KEY),
                  disabled,
                },
              ]}
            />
          </Stack>
          <VariantDetailsFormTable
            isControl={false}
            variants={treatmentVariants}
            onDelete={onDeleteVariant}
            onEdit={onEditVariant}
            onRemoveFlag={onRemoveFlag}
            disabled={disabled}
          />
        </Stack>
      </Stack>
      {variantDetailPanelIsOpen && (
        <VariantDetailPanel
          extensionPrefix={experimentFormContext.extensionPrefix}
          variantDetails={variantDetails}
          variantDetail={selectedVariant}
          experiments={experiments}
          isControl={selectedVariantType === VARIANT_DETAIL_CONTROL_KEY}
          isClone={isClone}
          onSave={onSaveVariant}
          onDismiss={dismissPanel}
        />
      )}
    </Stack>
  );
};

export default VariantDetailsForm;
